import React, { useEffect, useRef, useState } from 'react';
import styles from './Dashboard.module.css';
import { faArrowsRotate } from '@fortawesome/free-solid-svg-icons'; 
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/Auth/AuthContext';
import { getApiUrl } from '../../config/api';

// Define the Job type.
interface Job {
  jobId: string;
  filename?: string;
  status: 'queued' | 'processing' | 'completed' | 'failed' | 'chunk-uploading' | 'assembling' | 'mp4-completed' | 's3-completed' | string;
  percentage?: number;
  startTime?: number;
  time?: string;
  duration?: string;
  fps?: string;
  bitrate?: string;
  speed?: string;
  error?: string;
  outputPath?: string;
  projectId?: string;
  stage?: string;
  message?: string;
  displayMessage?: string; // Combined stage|message from backend
  // Chunked upload specific fields
  currentChunk?: number;
  totalChunks?: number;
  bytesUploaded?: number;
  totalBytes?: number;
  sessionId?: string;
}

interface DashboardProps {
  jobId: string;
  redirectUrl?: string; // Add redirectUrl prop
  callBackFunc:(videoUrl:string, projectId:string)=>void;
  displayOption?: 'detailed' | 'compact' | 'fullview'; // Added fullview option
  onProcessingStateChange?: (isProcessing: boolean) => void; // Add processing state callback
}

const Dashboard: React.FC<DashboardProps> = ({ jobId, redirectUrl, callBackFunc, onProcessingStateChange,displayOption="compact" }) => {
  const navigate = useNavigate();
  const { token } = useAuth(); // Add auth context
  const [job, setJob] = useState<Job | null>(null);
  const jobIdRef = useRef<string>(jobId); // Store jobId in a ref to persist between renders
  const intervalIdRef = useRef<NodeJS.Timeout | null>(null); // Store intervalId in a ref
  const [isHovering, setIsHovering] = useState<boolean>(false);

  // Fetch job status by ID
  const fetchJobStatus = async () => {
    if (!jobIdRef.current) return;
    
    try {
      console.log('📊 Fetching job status for ID:', jobIdRef.current);
      
      // Get authentication headers
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      
      // Add authorization header if token exists
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(getApiUrl(`/video/status/${jobIdRef.current}`), {
        headers
      });
      
      if (!response.ok) {
        console.error('❌ Failed to fetch job status:', response.status, response.statusText);
        throw new Error(`Failed to fetch job status: ${response.status}`);
      }
      
      const jobData: Job = await response.json();
      console.log('📈 Job status received:', jobData);
      setJob(jobData);
      
      // Notify parent about processing state
      if (onProcessingStateChange) {
        const isProcessing = jobData.status === 'processing' || 
                           jobData.status === 'chunk-uploading' || 
                           jobData.status === 'assembling' ||
                           (jobData.status !== 'completed' && jobData.status !== 'failed');
        console.log(`🔧 DEBUG: Setting processing state to ${isProcessing} (status: ${jobData.status})`);
        onProcessingStateChange(isProcessing);
      }
      
      // Handle redirect when job is completed or percentage is 100
      // ✅ Special handling for cleanup jobs (no outputPath required)
      const isCleanupJob = jobData.message === 'Cleanup complete!' || 
                          jobData.stage === 'AI Video Cleanup' ||
                          jobData.displayMessage?.includes('Cleanup complete');
      
      if ((jobData.status === 'completed' || jobData.percentage === 100) && jobData.projectId) {
        // For cleanup jobs, outputPath is not required
        if (isCleanupJob || jobData.outputPath) {
          // Clear the interval before redirecting
          if (intervalIdRef.current) {
            clearInterval(intervalIdRef.current);
          }
          
          // Call callback with outputPath (may be empty for cleanup jobs)
          callBackFunc(jobData.outputPath || '', jobData.projectId);
        }
      }
    } catch (error) {
      console.error('Error fetching job status:', error);
    }
  };

  // Manual refresh function
  const refreshJob = () => {
    fetchJobStatus();
  };

  // Initialize job fetching and start the interval
  useEffect(() => {
    // Update ref if prop changes
    if (jobId) {
      jobIdRef.current = jobId;
    }
    
    // Initial fetch
    fetchJobStatus();
    
    // Set up the interval for polling
    intervalIdRef.current = setInterval(fetchJobStatus, 5000); // Poll every 5 seconds
    
    // Cleanup function
    return () => {
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current);
      }
    };
  }, [jobId, redirectUrl]); // Re-run if these props change

  // Calculate time elapsed
  const timeElapsed = job?.startTime 
    ? Math.floor((Date.now() - job.startTime) / 1000) 
    : 0;

  // Helper function to get stage and message from job data
  const getStageAndMessage = () => {
    if (job?.displayMessage) {
      const parts = job.displayMessage.split('|');
      return { stage: parts[0], message: parts[1] || '' };
    } else if (job?.stage && job?.message) {
      return { stage: job.stage, message: job.message };
    } else {
      // Handle chunked upload stages
      switch (job?.status) {
        case 'chunk-uploading':
          if (job.currentChunk && job.totalChunks) {
            return { 
              stage: 'Chunk Upload', 
              message: `Uploading chunk ${job.currentChunk} of ${job.totalChunks}` 
            };
          }
          return { stage: 'Chunk Upload', message: 'Uploading file chunks...' };
        case 'assembling':
          return { stage: 'File Assembly', message: 'Combining uploaded chunks...' };
        case 'mp4-completed':
          return { stage: 'Video Processing', message: 'MP4 conversion completed' };
        case 's3-completed':
          return { stage: 'Cloud Upload', message: 'File uploaded to cloud storage' };
        default:
          return { stage: job?.status || 'Unknown', message: '' };
      }
    }
  };
  
  // Helper function to get progress bar color based on stage
  const getProgressBarColor = () => {
    switch (job?.status) {
      case 'chunk-uploading':
        return '#3b82f6'; // Blue for chunked upload
      case 'assembling':
        return '#8b5cf6'; // Purple for assembly
      case 'processing':
        return '#f59e0b'; // Amber for processing
      case 'mp4-completed':
      case 's3-completed':
        return '#10b981'; // Green for completed stages
      case 'completed':
        return '#059669'; // Darker green for final completion
      case 'failed':
        return '#ef4444'; // Red for failures
      default:
        return '#6b7280'; // Gray for unknown
    }
  };

  // Helper function to format file size
  const formatFileSize = (bytes: number | undefined): string => {
    if (!bytes) return '';
    
    const units = ['B', 'KB', 'MB', 'GB'];
    let size = bytes;
    let unitIndex = 0;
    
    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex++;
    }
    
    return `${size.toFixed(1)} ${units[unitIndex]}`;
  };

  // Render Option 1: Detailed View (Default)
  const renderDetailedView = () => {
    if (!job || !job.jobId) {
      return <div className={styles.loading}>Loading job information...</div>;
    }
    
    const { stage, message } = getStageAndMessage();
    const progressColor = getProgressBarColor();
    
    return (
      <div className={styles.dashboard}>
        <div key={job.jobId} className={`${styles.jobCard} ${styles[job.status]}`}>
          <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', fontSize: '0.8em', marginBottom: '-1em' }}>
            <h3>{job.filename || 'Unnamed Job'}</h3>
            <div className={styles.jobActions}>
              <FontAwesomeIcon className={styles.refreshBtn} onClick={refreshJob} icon={faArrowsRotate} /> 
              {job.status === 'completed' && (
                <a href={job.outputPath}>
                  <button className={styles.downloadBtn}>Download</button>
                </a>
              )}
            </div>
          </div>
          <span style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', fontSize: '0.8em', marginBottom: '-0.5em' }}>
            <p>Status: <strong>{job.status}</strong></p>
            <p>Progress: <strong>{job.percentage || 0}%</strong></p>
          </span>
          
          {/* Display stage and detailed message */}
          <div className={styles.processingDetails}>
            <p className={styles.stageMessage}>
              <strong>{stage}</strong>: {message}
            </p>
          </div>
          
          {/* Enhanced progress bar with chunked upload info */}
          <div className={styles.progressBar}>
            <div 
              className={styles.progressFill} 
              style={{ 
                width: `${job.percentage || 0}%`,
                backgroundColor: progressColor 
              }} 
            />
          </div>
          
          {/* Chunked upload specific information */}
          {(job.status === 'chunk-uploading' || job.status === 'assembling') && (
            <div className={styles.chunkedInfo} style={{
              fontSize: '0.8em',
              color: '#6c757d',
              marginTop: '0.5rem',
              display: 'flex',
              justifyContent: 'space-between'
            }}>
              {job.currentChunk && job.totalChunks && (
                <span>Chunk: {job.currentChunk}/{job.totalChunks}</span>
              )}
              {job.bytesUploaded && job.totalBytes && (
                <span>
                  {formatFileSize(job.bytesUploaded)} / {formatFileSize(job.totalBytes)}
                </span>
              )}
            </div>
          )}
          
          {job.error && <p className={styles.error}>Error: {job.error}</p>}
          
          {/* Display technical information if available */}
          {(job.fps || job.speed || job.time) && (
            <div className={styles.technicalInfo}>
              {job.fps && <span>FPS: {job.fps}</span>}
              {job.speed && <span>Speed: {job.speed}</span>}
              {job.time && <span>Time: {job.time}</span>}
            </div>
          )}
        </div>
      </div>
    );
  };

  // Render Option 2: Compact View
  const renderCompactView = () => {
    if (!job || !job.jobId) {
      return <div className={styles.loading}>Loading job information...</div>;
    }
    
    const { stage, message } = getStageAndMessage();
    const displayText = message ? `${stage}: ${message}` : stage;
    const percentage = job.percentage || 0;
    const progressColor = getProgressBarColor();
    
    return (
      <div className={`${styles.compactView} ${styles[job.status]}`}>
        
        { !job.error && 
          <div 
            className={styles.compactProgressBarContainer}
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
          >
            {/* Actual progress bar */}
            <div className={styles.compactProgressBar}>
              <div 
                className={styles.compactProgressFill} 
                style={{ 
                  width: `${percentage}%`,
                  backgroundColor: progressColor
                }}
              />
            </div>
            
            {/* Status information - visible on hover or for chunked uploads */}
            {(isHovering || job.status === 'chunk-uploading' || job.status === 'assembling') && (
              <div className={styles.hoverInfo} style={{ backgroundColor: `transparent`, width: '98%' }}>
                <div className={styles.statusMessage}>
                  {displayText}
                  {/* Show chunk info for chunked uploads */}
                  {job.currentChunk && job.totalChunks && (
                    <span style={{ fontSize: '0.8em', marginLeft: '0.5rem', opacity: 0.8 }}>
                      ({job.currentChunk}/{job.totalChunks})
                    </span>
                  )}
                </div>
                <div className={styles.percentageText}>
                  {percentage}%
                  {/* Show file size info if available */}
                  {job.bytesUploaded && job.totalBytes && (
                    <span style={{ fontSize: '0.7em', display: 'block', opacity: 0.8 }}>
                      {formatFileSize(job.bytesUploaded)} / {formatFileSize(job.totalBytes)}
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        }
        {job.error && <p style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} className={styles.error}>Error: {job.error}</p>}
      </div>
    );
  };

  // Render Option 3: Full View
  const renderFullView = () => {
    if (!job || !job.jobId) {
      return (
        <div style={{ 
          height: '8px',
          backgroundColor: '#666',
          width: '100%'
        }}>
          Loading...
        </div>
      );
    }
    
    const { stage, message } = getStageAndMessage();
    const displayText = message ? `${stage}: ${message}` : stage;
    const percentage = job.percentage || 0;
    const progressColor = getProgressBarColor();
    
    return (
      <div 
        style={{ 
          position: 'relative',
          width: '100%',
          height: isHovering ? '8px' : '8px', // Grows on hover
          backgroundColor: '#333',
          cursor: 'pointer',
          transition: 'height 0.2s ease' // Smooth height transition
        }}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
      >
        {/* Progress bar fill */}
        <div 
          style={{ 
            width: `${percentage}%`,
            height: '100%',
            backgroundColor: '#f59e0b', // Orange color
            transition: 'width 0.5s ease'
          }}
        />
        
        {/* Hover tooltip with detailed info */}
        {isHovering && (
          <div style={{
            position: 'absolute',
            top: '0px',
            left: '10px',
            right: '10px',
            backgroundColor: '#2a2a2a',
            color: '#e0e0e0',
            //padding: '0px 12px 0px 0px',
            margin: '0 -0px 0 -12px',
            width: '100%',
            borderRadius: '6px',
            fontSize: '8px',
            zIndex: 1000,
            boxShadow: '0 4px 12px rgba(0,0,0,0.6)',
            border: '1px solid #444',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            height: '8px',
            animation: 'fadeIn 0.2s ease'
          }}>
            <div style={{ display: 'flex', marginLeft: '12px'}}>
              {/* <div style={{ fontWeight: 'bold', marginBottom: '2px' }}>
                Job #{job.jobId}
              </div> */}
              <div style={{ opacity: 0.9 }}>
                {displayText}
                {job.currentChunk && job.totalChunks && (
                  <span style={{ marginLeft: '12px', opacity: 0.7 }}>
                    (Chunk {job.currentChunk}/{job.totalChunks})
                  </span>
                )}
              </div>
              {job.bytesUploaded && job.totalBytes && (
                <div style={{ fontSize: '10px', opacity: 0.7, marginTop: '2px' }}>
                  {formatFileSize(job.bytesUploaded)} / {formatFileSize(job.totalBytes)}
                </div>
              )}
            </div>
            <div style={{ 
              fontSize: '8px', 
              fontWeight: 'bold', 
              color: '#f59e0b',
              marginRight: '12px'
            }}>
              {percentage}%
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={styles.dashboardContainer}>
      {displayOption === 'detailed' ? renderDetailedView() : 
       displayOption === 'fullview' ? renderFullView() : 
       renderCompactView()}
    </div>
  );
};

// Helper function to format time in HH:MM:SS format.
function formatTime(seconds: number): string {
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}

export default Dashboard;